/**
 * Style combo — chains skill events within a short window for multiplier buildup.
 * All client-side; works alongside Colyseus / any netcode (no server dependency).
 */

export type ComboEventKind =
  | 'near_miss'
  | 'drift_chain'
  | 'pursuit_breaker'
  | 'takedown'
  | 'heat_up';

export interface ComboState {
  chain: number;
  /** Seconds until chain resets if no new event. */
  decay: number;
  /** Highest multiplier reached this run (for leaderboards). */
  peakMultiplier: number;
  /** Sum of all combo-weighted style points. */
  totalStylePoints: number;
}

const CHAIN_WINDOW_S = 3.35;

const BASE_POINTS: Record<ComboEventKind, number> = {
  near_miss: 100,
  drift_chain: 250,
  pursuit_breaker: 500,
  takedown: 400,
  heat_up: 150,
};

export function createComboState(): ComboState {
  return {
    chain: 0,
    decay: 0,
    peakMultiplier: 1,
    totalStylePoints: 0,
  };
}

function multiplierForChain(chain: number): number {
  if (chain <= 0) return 1;
  return Math.min(5, 1 + (chain - 1) * 0.38);
}

export function updateComboDecay(state: ComboState, dt: number): void {
  if (state.decay > 0) {
    state.decay -= dt;
    if (state.decay <= 0) {
      state.chain = 0;
      state.decay = 0;
    }
  }
}

export type ComboScoreResult = {
  awarded: number;
  multiplier: number;
  chain: number;
  basePoints: number;
};

/**
 * Register a scoring event; advances chain if within window (handled by caller refresh).
 */
export function scoreComboEvent(
  state: ComboState,
  kind: ComboEventKind,
  overrideBase?: number,
): ComboScoreResult {
  const base = overrideBase ?? BASE_POINTS[kind];
  state.chain += 1;
  state.decay = CHAIN_WINDOW_S;
  const multiplier = multiplierForChain(state.chain);
  state.peakMultiplier = Math.max(state.peakMultiplier, multiplier);
  const awarded = Math.floor(base * multiplier);
  state.totalStylePoints += awarded;
  return {
    awarded,
    multiplier,
    chain: state.chain,
    basePoints: base,
  };
}

export function formatComboSub(r: ComboScoreResult): string {
  return `${r.awarded} pts · ${r.multiplier.toFixed(2)}× · chain ${r.chain}`;
}

/** HUD readout — multiplier for current chain (even before next hit). */
export function getComboHudDisplay(state: ComboState): {
  mult: number;
  chain: number;
  active: boolean;
} {
  const active = state.chain > 0 && state.decay > 0;
  return {
    mult: multiplierForChain(state.chain),
    chain: state.chain,
    active,
  };
}
